import { useState, useRef, useCallback } from 'react';
import { Upload, X, AlertCircle, CheckCircle, Loader2, Image as ImageIcon } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Hero image constraints (matches server-side)
const HERO_CONSTRAINTS = {
  maxWidth: 1920,
  maxHeight: 1080,
  minWidth: 1280,
  minHeight: 720,
  maxSizeBytes: 500 * 1024, // 500KB
  allowedFormats: ['image/webp', 'image/jpeg', 'image/png'],
  aspectRatio: { width: 16, height: 9 },
};

interface HeroImageUploadProps {
  slideId: string;
  currentImage?: string;
  onUploadComplete?: (url: string) => void;
  alt?: string;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export function HeroImageUpload({
  slideId,
  currentImage,
  onUploadComplete,
  alt,
}: HeroImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [isUploading, setIsUploading] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateImage = useCallback((file: File): Promise<ValidationResult> => {
    return new Promise((resolve) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      // Check format
      if (!HERO_CONSTRAINTS.allowedFormats.includes(file.type)) {
        errors.push(`Invalid format. Use WebP, JPEG, or PNG`);
      }

      // Check file size
      if (file.size > HERO_CONSTRAINTS.maxSizeBytes) {
        const sizeKB = Math.round(file.size / 1024);
        const maxKB = HERO_CONSTRAINTS.maxSizeBytes / 1024;
        errors.push(`File too large (${sizeKB}KB). Maximum: ${maxKB}KB`);
      } else if (file.size > HERO_CONSTRAINTS.maxSizeBytes * 0.8) {
        warnings.push('File size is close to the limit. Consider optimizing.');
      }

      // Load image to check dimensions
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        setDimensions({ width: img.width, height: img.height });

        // Check dimensions
        if (img.width > HERO_CONSTRAINTS.maxWidth) {
          errors.push(`Width too large (${img.width}px). Max: ${HERO_CONSTRAINTS.maxWidth}px`);
        }
        if (img.height > HERO_CONSTRAINTS.maxHeight) {
          errors.push(`Height too large (${img.height}px). Max: ${HERO_CONSTRAINTS.maxHeight}px`);
        }
        if (img.width < HERO_CONSTRAINTS.minWidth) {
          errors.push(`Width too small (${img.width}px). Min: ${HERO_CONSTRAINTS.minWidth}px`);
        }
        if (img.height < HERO_CONSTRAINTS.minHeight) {
          errors.push(`Height too small (${img.height}px). Min: ${HERO_CONSTRAINTS.minHeight}px`);
        }

        // Check aspect ratio
        const expectedRatio = HERO_CONSTRAINTS.aspectRatio.width / HERO_CONSTRAINTS.aspectRatio.height;
        const actualRatio = img.width / img.height;
        const tolerance = 0.05;

        if (Math.abs(actualRatio - expectedRatio) > tolerance) {
          const actualRatioStr = `${img.width}:${img.height}`;
          errors.push(`Invalid aspect ratio (${actualRatioStr}). Expected 16:9`);
        }

        resolve({ valid: errors.length === 0, errors, warnings });
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        errors.push('Could not read image file');
        resolve({ valid: false, errors, warnings });
      };

      img.src = objectUrl;
    });
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    setUploadError(null);
    setValidation(null);
    setDimensions(null);

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    // Validate
    const result = await validateImage(file);
    setValidation(result);

    if (!result.valid) {
      return;
    }

    // Upload
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('slideId', slideId);
      if (dimensions) {
        formData.append('width', String(dimensions.width));
        formData.append('height', String(dimensions.height));
      }
      if (alt) formData.append('alt', alt);

      const response = await fetch(`${API_URL}/admin/images/hero`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Upload failed');
      }

      onUploadComplete?.(data.data.url);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [slideId, dimensions, alt, validateImage, onUploadComplete]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleRemove = useCallback(async () => {
    // Persist the removal: clear the slide's image in R2 + DB
    if (slideId) {
      try {
        await fetch(`${API_URL}/admin/hero-slides/${slideId}/image`, {
          method: 'DELETE',
          credentials: 'include',
        });
      } catch (e) {
        console.error('Failed to remove hero image', e);
      }
    }
    setPreview(null);
    setValidation(null);
    setDimensions(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [slideId]);

  return (
    <div className="space-y-4">
      {/* Requirements info */}
      <div className="bg-gray-50 rounded-lg p-3 text-sm">
        <div className="font-medium text-gray-700 mb-1">Image Requirements:</div>
        <ul className="text-gray-500 space-y-0.5">
          <li>• Dimensions: 1280-1920px wide, 720-1080px tall</li>
          <li>• Aspect ratio: 16:9</li>
          <li>• Max file size: 500KB</li>
          <li>• Formats: WebP (recommended), JPEG, PNG</li>
        </ul>
      </div>

      {/* Drop zone */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={`
          relative border-2 border-dashed rounded-xl p-6 transition-colors cursor-pointer
          ${validation?.valid === false
            ? 'border-red-300 bg-red-50'
            : validation?.valid === true
              ? 'border-green-300 bg-green-50'
              : 'border-gray-300 hover:border-gold-400 hover:bg-gold-50'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/webp,image/jpeg,image/png"
          onChange={handleInputChange}
          className="hidden"
        />

        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg"
            />
            {dimensions && (
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {dimensions.width} x {dimensions.height}
              </div>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRemove(); }}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <X className="w-4 h-4" />
            </button>
            {isUploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                <div className="flex items-center gap-2 text-white">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading...
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
              {currentImage ? (
                <ImageIcon className="w-6 h-6 text-gray-400" />
              ) : (
                <Upload className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium text-gold-600">Click to upload</span> or drag and drop
            </div>
            <div className="text-xs text-gray-400 mt-1">
              WebP, JPEG or PNG (max 500KB)
            </div>
          </div>
        )}
      </div>

      {/* Validation messages */}
      {validation && (
        <div className="space-y-2">
          {validation.errors.map((error, i) => (
            <div key={i} className="flex items-start gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          ))}
          {validation.warnings.map((warning, i) => (
            <div key={i} className="flex items-start gap-2 text-amber-600 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {warning}
            </div>
          ))}
          {validation.valid && validation.errors.length === 0 && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <CheckCircle className="w-4 h-4" />
              Image meets all requirements
            </div>
          )}
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {uploadError}
        </div>
      )}
    </div>
  );
}
